import type { KeyboardEventHandler } from 'react';

import Icon from 'components/Icon';
import PlainButton from 'components/PlainButton';
import { type FieldKey, useDisclosureWidgetClose, useRefObject } from 'hooks';
import type { InspirationLink, MainLinkFields } from 'models/sitecore';
import type { ButtonHTMLAttributesWithRef, TemplateString } from 'types';
import { cn } from 'utils/classNames';
import { slugify } from 'utils/string';

import PageHeaderDropdown from './PageHeaderDropdown';
import type { DropdownLinkClickHandler } from './PageHeaderLinkListItem';

export type CloseHandler = (linkText: string) => void;
export type OpenHandler = (linkText: string) => void;

interface Props extends ButtonHTMLAttributesWithRef<HTMLButtonElement> {
	className?: string;
	dropdownLinks: MainLinkFields[];
	inspirationLink: InspirationLink | undefined;
	inspirationLinksHeading: string;
	inspirationMediaHeading: string;
	isLargeScreen: boolean | undefined;
	isOpen?: boolean;
	navigationCategoryShowAllButton: TemplateString | FieldKey;
	onClose: CloseHandler;
	onLargeScreenMenuLastLinkTab: KeyboardEventHandler<HTMLAnchorElement>;
	onLinkClick: DropdownLinkClickHandler;
	onOpen: OpenHandler;
	onToggledMenuLastLinkTab: KeyboardEventHandler<HTMLAnchorElement>;
	popularProductsText: string;
	searchPopularSearchTermsText: string;
	selectedDropdownLinkId: string;
	text: string;
}

/** A top level main menu item that has a dropdown menu. */
export default function PageHeaderDropdownToggle({
	className,
	dropdownLinks,
	inspirationLink,
	inspirationLinksHeading,
	inspirationMediaHeading,
	isLargeScreen,
	isOpen = false,
	navigationCategoryShowAllButton,
	onClose,
	onLargeScreenMenuLastLinkTab,
	onLinkClick,
	onOpen,
	onToggledMenuLastLinkTab,
	popularProductsText,
	ref,
	searchPopularSearchTermsText,
	selectedDropdownLinkId,
	text,
	...attrs
}: Props) {
	const triggerRef = useRefObject<HTMLButtonElement>(ref);
	const dropdownId = `header-dropdown-${slugify(text)}`;

	const handleClose = () => {
		onClose(text);
	};

	const {
		onTriggerClick,
		onTriggerMouseDown,
		widgetRef: dropdownRef,
	} = useDisclosureWidgetClose<HTMLButtonElement, HTMLDivElement>(
		// Not active when the menu is in its small screen toggled form.
		Boolean(isOpen && isLargeScreen),
		handleClose,
		{ triggerRef },
	);

	const handleTriggerClick = () => {
		onTriggerClick();
		if (isOpen) {
			onClose(text);
		} else {
			onOpen(text);
		}
	};

	return (
		<>
			<PlainButton
				{...attrs}
				ref={triggerRef}
				aria-controls={dropdownId}
				aria-expanded={isOpen}
				className={className}
				onClick={handleTriggerClick}
				onMouseDown={onTriggerMouseDown}
			>
				{text}
				<Icon
					name={
						// Default to up/down when the screen size isn't yet determined
						// (undefined). The arrow is visible on page load on large screens
						// while it's hidden behind a menu toggle on small screens. Having
						// it mobile first (right by default) means it will point right
						// and then rotate down on large screens when loading.
						isLargeScreen === false ? 'chevron-right' : 'chevron-down'
					}
					className={cn(
						'ml-2 transition-transform',
						isLargeScreen && isOpen && 'rotate-180',
					)}
				/>
			</PlainButton>
			<PageHeaderDropdown
				ref={dropdownRef}
				id={dropdownId}
				inspirationLink={inspirationLink}
				inspirationMediaHeading={inspirationMediaHeading}
				inspirationLinksHeading={inspirationLinksHeading}
				isLargeScreen={isLargeScreen}
				isOpen={isOpen}
				links={dropdownLinks}
				navigationCategoryShowAllButton={navigationCategoryShowAllButton}
				onLinkClick={onLinkClick}
				onLargeScreenMenuLastLinkTab={onLargeScreenMenuLastLinkTab}
				onToggledMenuLastLinkTab={onToggledMenuLastLinkTab}
				popularProductsText={popularProductsText}
				searchPopularSearchTermsText={searchPopularSearchTermsText}
				selectedLinkId={selectedDropdownLinkId}
				title={text}
			/>
		</>
	);
}
PageHeaderDropdownToggle.displayName = 'PageHeaderDropdownToggle';
